/**
 * 多标签页详情面板组件
 *
 * 类似 VS Code 的多标签页设计，支持同时打开多个章节/设定
 * - 顶部标签栏显示名称+关闭按钮
 * - 支持点击切换标签
 * - 所有标签关闭后显示空白页
 */

import React from 'react';
import type { Chapter } from '../../types/chapter';
import type { Setting } from '../../types/setting';

// ==================== 类型定义 ====================

export type TabType = 'chapter' | 'setting';

export interface Tab {
  id: string;
  type: TabType;
  title: string;
  data: Chapter | Setting;
  isModified?: boolean;
}

interface TabbedDetailPanelProps {
  /** 打开的标签页列表 */
  tabs: Tab[];
  /** 当前激活的标签页ID */
  activeTabId: string | null;
  /** 切换标签页回调 */
  onTabChange: (tabId: string) => void;
  /** 关闭标签页回调 */
  onTabClose: (tabId: string) => void;
  /** 关闭整个面板回调（点击空白页的关闭按钮） */
  onPanelClose: () => void;
  /** 更新标签页数据回调 */
  onTabDataUpdate: (tabId: string, data: Chapter | Setting) => void;
  /** 章节内容渲染函数 */
  renderChapter: (chapter: Chapter, onUpdate: (chapter: Chapter | null) => void) => React.ReactNode;
  /** 设定内容渲染函数 */
  renderSetting: (setting: Setting, onUpdate: (setting: Setting | null) => void) => React.ReactNode;
}

// ==================== SVG 图标 ====================

const CloseIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const EmptyIcon = () => (
  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" stroke-linejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// ==================== 子组件 ====================

interface TabBarProps {
  tabs: Tab[];
  activeTabId: string | null;
  onTabChange: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
}

/**
 * 标签栏组件
 */
const TabBar: React.FC<TabBarProps> = ({ tabs, activeTabId, onTabChange, onTabClose }) => {
  if (tabs.length === 0) return null;

  return (
    <div className="flex items-center border-b border-gray-200 bg-gray-50 overflow-x-auto">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`
              group flex items-center gap-2 px-3 py-2 text-sm cursor-pointer
              border-r border-gray-200 min-w-[120px] max-w-[200px]
              transition-colors duration-150
              ${isActive 
                ? 'bg-white text-gray-900 border-b-2 border-b-gray-800 -mb-[1px]' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
              }
            `}
          >
            {/* 图标 */}
            <span className="flex-shrink-0">
              {tab.type === 'chapter' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
            </span>
            
            {/* 标题 */}
            <span className="flex-1 truncate select-none">
              {tab.title}
              {tab.isModified && <span className="ml-1 text-gray-400">●</span>}
            </span>
            
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTabClose(tab.id);
              }}
              className={`
                flex-shrink-0 p-0.5 rounded transition-colors
                ${isActive 
                  ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-200' 
                  : 'text-gray-300 hover:text-gray-500 hover:bg-gray-200 opacity-0 group-hover:opacity-100'
                }
              `}
              title="关闭标签页"
            >
              <CloseIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
};

interface EmptyPageProps {
  onClose: () => void;
}

/**
 * 空白页组件
 * 当所有标签页关闭时显示
 */
const EmptyPage: React.FC<EmptyPageProps> = ({ onClose }) => {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-white p-8 relative">
      {/* 关闭按钮 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="关闭中间面板"
        title="关闭中间面板"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <div className="text-center max-w-md">
        <EmptyIcon />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">工作区</h2>
        <p className="text-gray-600 mb-4">
          在左侧面板中选择章节或设定开始创作。
        </p>
        <p className="text-sm text-gray-500">
          支持同时打开多个标签页，方便在不同内容间切换。
        </p>
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

/**
 * 多标签页详情面板组件
 */
export const TabbedDetailPanel: React.FC<TabbedDetailPanelProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  onTabClose,
  onPanelClose,
  onTabDataUpdate,
  renderChapter,
  renderSetting,
}) => {
  // 获取当前激活的标签页
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  // 处理章节更新
  const handleChapterUpdate = (chapter: Chapter | null) => {
    if (chapter && activeTabId) {
      onTabDataUpdate(activeTabId, chapter);
    }
  };

  // 处理设定更新
  const handleSettingUpdate = (setting: Setting | null) => {
    if (setting && activeTabId) {
      onTabDataUpdate(activeTabId, setting);
    }
  };

  // 如果没有打开的标签页，显示空白页
  if (tabs.length === 0 || !activeTab) {
    return <EmptyPage onClose={onPanelClose} />;
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 标签栏 */}
      <TabBar
        tabs={tabs}
        activeTabId={activeTabId}
        onTabChange={onTabChange}
        onTabClose={onTabClose}
      />
      
      {/* 内容区域 */}
      <div className="flex-1 overflow-hidden">
        {activeTab.type === 'chapter' ? (
          renderChapter(activeTab.data as Chapter, handleChapterUpdate)
        ) : (
          renderSetting(activeTab.data as Setting, handleSettingUpdate)
        )}
      </div>
    </div>
  );
};

export default TabbedDetailPanel;
